import { lazy, Suspense } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Activity, TrendingUp, Shield } from "lucide-react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { riseFade } from "@/lib/motion";
import PillarSeo from "@/components/seo/PillarSeo";
import RelatedPillars from "@/components/seo/RelatedPillars";
import ShareButtons from "@/components/blog/ShareButtons";
import BookmarkButton from "@/components/blog/BookmarkButton";
import RelatedArticles from "@/components/blog/RelatedArticles";

const CheckinSimulator = lazy(() => import("@/components/marketing/CheckinSimulator"));

const META_TITLE = "MS Fatigue: Predict Multiple Sclerosis Fatigue Day-by-Day | Myelina Health";
const META_DESC =
  "MS fatigue is different from ordinary tiredness. Myelina Health is a multiple sclerosis fatigue tracker that predicts your energy windows the night before, about-a-minute check-in, by invitation.";

const FAQS: { question: string; answer: string }[] = [
  {
    question: "What does an MS fatigue tracker actually do?",
    answer:
      "It captures a short daily check-in, optionally reads your wearable's HRV, sleep, and resting heart-rate signals, and learns which combinations of inputs precede your good and bad days. After 2–3 weeks of use, it can predict the day's energy windows the night before.",
  },
  {
    question: "How is Myelina Health different from a journal or a generic symptom tracker?",
    answer:
      "Journals record what already happened. Myelina, the agent inside Myelina Health, predicts what is likely to happen, and gives you one sentence about where to spend your energy. It also scores PROMIS Fatigue-MS 8a so the data is meaningful to your clinical team.",
  },
  {
    question: "Do I need a wearable to use it?",
    answer:
      "No. A about-a-minute morning check-in alone gives you a useful daily forecast. Connecting an Oura Ring, Apple Watch, Fitbit, or Garmin sharpens the prediction and shortens the learning curve.",
  },
  {
    question: "Is my data private?",
    answer:
      "Yes. You control your data. Clinician sharing is opt-in per visit. Data is encrypted in transit and at rest with access controls. You can export or delete everything in one click. We never sell your data.",
  },
  {
    question: "How do I get access?",
    answer:
      "Myelina Health is invite only during our current cohort. Request access from any page and we'll reach out when a spot opens. For partnerships or hardship access, email hello@myelinahealth.com.",
  },
  {
    question: "Does multiple sclerosis cause fatigue?",
    answer:
      "Yes, fatigue is one of the most common MS symptoms, reported by up to 80% of people with MS. It can be caused by the disease process itself (lesions affecting fatigue-regulating pathways), by poor sleep from spasticity or bladder issues, by depression, by medications, or by the cognitive load of compensating for other symptoms. Tracking which combinations precede your worst days is how you separate causes.",
  },
  {
    question: "What does multiple sclerosis fatigue feel like?",
    answer:
      "MS fatigue is qualitatively different from ordinary tiredness. People describe it as a sudden full-body heaviness, a leaden quality in the limbs, brain fog that makes simple sentences hard to assemble, and a sensation that the battery is at 5% even after eight hours of sleep. It often gets worse with heat and as the day goes on.",
  },
  {
    question: "How can I manage fatigue with multiple sclerosis?",
    answer:
      "The evidence-supported playbook: keep sleep and wake times consistent, manage heat aggressively (cool drinks, cooling vests, AC), schedule the most demanding tasks inside your real energy window, take planned rests before you dip (not after), stay hydrated, and treat any concurrent depression or sleep apnea. A daily forecast makes the scheduling part possible.",
  },
  {
    question: "How do you beat fatigue with multiple sclerosis?",
    answer:
      "You don't beat it, you out-plan it. The goal isn't to feel less tired; it's to spend the energy you have where it matters before the dip arrives. That's the entire bet behind a predictive fatigue tracker.",
  },
  {
    question: "What helps with multiple sclerosis fatigue?",
    answer:
      "In order of evidence strength: cooling and heat avoidance, sleep hygiene, exercise dosed to your tolerance (yes, even when tired, graded activity helps), CBT for fatigue, treating coexisting depression, and medications like modafinil or amantadine when prescribed. A daily forecast helps you apply the right intervention at the right time.",
  },
  {
    question: "Why does multiple sclerosis cause fatigue?",
    answer:
      "Research points to several overlapping mechanisms: inflammation-driven changes in the central nervous system, disrupted communication between brain regions that regulate effort, the metabolic cost of damaged nerves firing inefficiently, and the cognitive tax of constantly compensating for other symptoms. It's not one cause, which is why one-size-fits-all advice rarely works.",
  },
];

const MsFatigueTracker = () => {
  return (
    <>
      <PillarSeo
        slug="ms-fatigue-tracker"
        title={META_TITLE}
        description={META_DESC}
        faqs={FAQS}
        date="2026-05-17"
        image="/og/pillar-ms-fatigue-tracker.jpg"
        imageAlt="MS Fatigue Tracker | Predict your energy. Plan your day. | Myelina Health"
      />

    <div className="min-h-screen bg-background">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-12 sm:pt-44 sm:pb-16">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={riseFade}
              className="max-w-3xl mx-auto text-center space-y-6"
            >
              <div className="eyebrow justify-center mx-auto">For women living with MS</div>
              <h1 className="font-display text-foreground text-display-xl leading-[1.05]">
                MS fatigue, <span className="italic text-primary">predicted</span>, not just tracked.
              </h1>
              <p className="text-lg leading-[1.7] text-muted-foreground max-w-2xl mx-auto">
                Multiple sclerosis fatigue is different from ordinary tiredness, and it doesn't
                respond to willpower. Myelina Health is an MS fatigue tracker that reads a 60-second
                morning check-in and tells you, the night before, where tomorrow's energy windows
                will land, so you can plan a day that fits the body you actually have.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-2">
                <Link to="/demo">
                  <Button variant="premium" size="lg" className="group w-full sm:w-auto px-8">
                    See the demo
                    <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button variant="outline" size="lg" className="w-full sm:w-auto px-8">
                    Request access
                  </Button>
                </Link>
              </div>
              <ShareButtons slug="ms-fatigue-tracker" title={META_TITLE} summary={META_DESC} className="justify-center mt-6" />
              <div className="flex justify-center mt-3">
                <BookmarkButton slug="ms-fatigue-tracker" title="MS Fatigue Tracker" category="Pillar" kind="pillar" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* What it does */}
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-5xl mx-auto"
          >
            <h2 className="font-display text-3xl sm:text-4xl text-foreground text-center mb-12">
              What an MS fatigue tracker should actually do
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                {
                  icon: Activity,
                  title: "Capture without burden",
                  body: "A about-a-minute morning check-in. Three questions, three taps. Optional wearable signal (HRV, sleep, skin temperature) tightens the prediction without asking you to log anything else.",
                },
                {
                  icon: TrendingUp,
                  title: "Predict, not just record",
                  body: "After 2–3 weeks the system learns what your good and bad days look like, and forecasts tomorrow's energy windows the night before, calmly, in one sentence.",
                },
                {
                  icon: Shield,
                  title: "Translate to your clinic",
                  body: "PROMIS Fatigue-MS 8a scored from the same data. Patient-controlled opt-in sharing. Your team gets the longitudinal picture; nothing leaves without your consent.",
                },
              ].map((f) => (
                <Card key={f.title} className="p-6 sm:p-8 border-border/60 shadow-sm h-full">
                  <f.icon className="w-6 h-6 text-primary mb-3" />
                  <h3 className="font-display text-xl text-foreground mb-2">{f.title}</h3>
                  <p className="text-[15px] text-muted-foreground leading-relaxed">{f.body}</p>
                </Card>
              ))}
            </div>
          </motion.div>
        </section>

        {/* Live simulator */}
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-12 sm:py-16">
          <Suspense fallback={null}>
            <CheckinSimulator variant="page" />
          </Suspense>
        </section>

        {/* Why energy prediction matters */}
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-20">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-3xl mx-auto prose-like space-y-6"
          >
            <h2 className="font-display text-3xl sm:text-4xl text-foreground">
              Why predicting matters more than tracking
            </h2>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              Up to 80% of people with MS experience fatigue as a primary symptom. Unlike ordinary
              tiredness, it does not respond reliably to sleep, caffeine, or willpower. It is a
              moving target, driven by sleep architecture, ambient heat, hormonal cycle, recent
              activity load, and the next morning's HRV, and it is the symptom most likely to
              determine whether you keep a plan or cancel it.
            </p>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              A symptom tracker records what already happened. By the time you see the pattern, the
              week is over. A <strong>fatigue predictor</strong> shortens that loop. It tells you,
              the night before, where the day is likely to give and where it is likely to take, so the school run, the meeting, and dinner can be sequenced before the body decides
              for you.
            </p>
            <p className="text-[16px] leading-[1.8] text-foreground/85">
              That is the whole bet behind Myelina Health: the same data your wearable already collects,
              plus a about-a-minute check-in, is enough to surface a one-sentence forecast you can plan
              the day around. No graphs to interpret at 7 a.m. when you are already tired.
            </p>

            <h3 className="font-display text-2xl text-foreground mt-10">What's inside every morning</h3>
            <ul className="space-y-2 text-[15px] text-foreground/85">
              {[
                "Daily check-in and energy logging",
                "Basic weekly trend summary",
                "Export your data any time",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <h3 className="font-display text-2xl text-foreground mt-8">What unlocks on Plus / Pro</h3>
            <ul className="space-y-2 text-[15px] text-foreground/85">
              {[
                "Predicted energy windows for the next day",
                "Rest-day nudges (calm, the night before)",
                "Wearable integration with Oura, Apple Watch, Fitbit, Garmin",
                "Care-partner sharing (Pro)",
                "Doctor-ready visit summary scored on PROMIS Fatigue-MS 8a (Pro)",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5">
                  <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </motion.div>
        </section>

        {/* FAQ */}
        <section className="container mx-auto px-6 sm:px-10 lg:px-16 py-16 sm:py-24" aria-labelledby="pillar-faq">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-10% 0px" }}
            variants={riseFade}
            className="max-w-3xl mx-auto"
          >
            <h2 id="pillar-faq" className="font-display text-3xl sm:text-4xl text-foreground text-center mb-10">
              Frequently asked questions
            </h2>
            <dl className="divide-y divide-border/60">
              {FAQS.map((faq) => (
                <div key={faq.question} className="py-5">
                  <dt className="font-display italic text-foreground text-xl mb-2">
                    {faq.question}
                  </dt>
                  <dd className="text-[15px] text-muted-foreground leading-relaxed">{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </motion.div>
        </section>

        <RelatedPillars
          intro="Keep reading"
          links={[
            { to: "/ms-energy-tracking", label: "MS energy tracking" },
            { to: "/living-with-ms", label: "living with MS" },
          ]}
        />

        {/* CTA */}
        <RelatedArticles topics={["ms fatigue", "multiple sclerosis fatigue", "lassitude", "energy tracker"]} featuredSlugs={["ms-fatigue-management-complete-guide", "what-does-ms-fatigue-feel-like", "how-long-does-ms-fatigue-last"]} />
        <section className="bg-secondary py-20 sm:py-28">
          <div className="container mx-auto px-6 sm:px-10 lg:px-16 text-center">
            <h2 className="font-display text-3xl sm:text-4xl text-foreground mb-4">
              Stop tracking. Start <span className="italic text-primary">planning.</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl mx-auto mb-8">
              Three questions, no account. See the windows Myelina would surface for tomorrow.
            </p>
            <Link to="/demo">
              <Button variant="premium" size="lg" className="group">
                See the demo
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
    </>
  );
};

export default MsFatigueTracker;
